import {Injectable} from '@angular/core';
import {AbstractModelService} from '../model/abstract-model.service';
import {ApiClientService} from '../api-client/api-client.service';
import {Offer} from '../../models/offer';
import {Partner} from '../../models/partner/partner';
import {forkJoin, Observable, Subscriber} from 'rxjs';
import {map} from 'rxjs/operators';
import {PartnerOfferService} from '../partner-offer/partner-offer.service';
import {Paginator} from '../../tools/pagination/paginator';
import {TableSorter} from '../../tools/table-sorter/table-sorter';
import {TableFilter} from '../../tools/table-filter/table-filter';

@Injectable({
  providedIn: 'root'
})
export class OfferService extends AbstractModelService<Offer> {

  constructor(apiClient: ApiClientService, private partnerOfferService: PartnerOfferService) {
    super(apiClient, '/offer', Offer);
  }

  override findAll(
    params: any = {},
    paginator: Paginator | null = null,
    tableSorter: TableSorter | null = null,
    tableFilter: TableFilter | null = null
  ): Observable<Offer[]> {
    if (!params.sort && !tableSorter) {
      params.sort = 'name';
    }

    const observable = super.findAll(params, paginator, tableSorter, tableFilter);

    // A paginated call can only rely on the server-side order: re-sorting one page
    // client-side would break the ordering across pages.
    if (paginator || tableSorter) {
      return observable;
    }

    // Offer names start with an emoji, so the SQL sort on name groups them by emoji:
    // complete lists are re-sorted here on the emoji-stripped name instead.
    return observable.pipe(map((offers: Offer[]) => this.sortByName(offers)));
  }

  sortByName(offers: Offer[]): Offer[] {
    return offers.sort((a, b) => {
      if (a.name === null) return 1;
      if (b.name === null) return -1;

      const nameA = a.name.replace(/[^\p{L}\p{N}]/gu, '');
      const nameB = b.name.replace(/[^\p{L}\p{N}]/gu, '');

      return nameA.localeCompare(nameB, 'fr', {
        sensitivity: 'base'
      });
    });
  }

  findPartnerOffers(partner: Partner, parameters: any = {}): Observable<Offer[]> {
    return new Observable<Offer[]>((subscriber: Subscriber<Offer[]>) => {
      parameters['enabled'] = true;

      if (partner.isAffiliate()) {
        forkJoin([
          this.findAll({
            sort: 'name',
            targetAccountType: partner.accountType,
            requireApproval: false,
            partnerId: partner.id,
            ...parameters
          }),
          this.partnerOfferService.findAll({
            sort: 'offer.name',
            status: 'active',
            ...parameters
          })
        ]).subscribe(([offers, partnerOffers]) => {
          const data = offers ?? [];
          for (const partnerOffer of partnerOffers) {
            data.push(partnerOffer.offer ?? new Offer());
          }

          subscriber.next(this.sortByName(data));
        });
      } else {
        this.findAll({
          sort: 'name',
          targetAccountType: partner.accountType,
          ...parameters
        }).subscribe((offers: Offer[]) => {
          subscriber.next(offers);
        });
      }
    });
  }
}
